import { useState, useEffect } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useAuth } from '@/services/authentication.service'

const DISMISSED_KEY = 'android-install-prompt-dismissed'
const DELAY_AFTER_ONBOARDING_MS = 5 * 60 * 1000 // 5 minutes

let deferredPrompt: any = null

export default function AndroidInstallPrompt() {
    const { user } = useAuth()
    const [show, setShow] = useState(false)

    useEffect(() => {
        // Don't show until user has completed onboarding and been in the app for a bit
        if (!user?.onboardingComplete) return
        const accountAge = user.createdAt ? Date.now() - new Date(user.createdAt).getTime() : 0
        if (accountAge < DELAY_AFTER_ONBOARDING_MS) return

        const isStandalone = window.matchMedia('(display-mode: standalone)').matches
        const isDismissed = localStorage.getItem(DISMISSED_KEY)

        if (isStandalone || isDismissed) return

        const handler = (e: Event) => {
            e.preventDefault()
            deferredPrompt = e
            setTimeout(() => setShow(true), 2000)
        }

        window.addEventListener('beforeinstallprompt', handler)
        return () => window.removeEventListener('beforeinstallprompt', handler)
    }, [user?.onboardingComplete])

    const dismiss = () => {
        setShow(false)
        localStorage.setItem(DISMISSED_KEY, 'true')
    }

    const install = async () => {
        if (!deferredPrompt) return
        deferredPrompt.prompt()
        const { outcome } = await deferredPrompt.userChoice
        deferredPrompt = null
        if (outcome === 'accepted') {
            setShow(false)
            localStorage.setItem(DISMISSED_KEY, 'true')
        }
    }

    if (!show) return null

    return (
        <div className='fixed bottom-0 left-0 right-0 z-[60] safe-area-bottom animate-in slide-in-from-bottom duration-300'>
            <div className='mx-3 mb-3 bg-white rounded-2xl shadow-xl border border-neutral-200/60 p-4'>
                <div className='flex items-start gap-3'>
                    <img src='/pwa-192x192.png' alt='BrightPath' className='w-12 h-12 rounded-xl flex-shrink-0' />
                    <div className='flex-1 min-w-0'>
                        <p className='font-semibold text-neutral-900 text-sm'>Install BrightPath</p>
                        <p className='text-xs text-neutral-500 mt-0.5'>
                            Add to your home screen for quick access
                        </p>
                        <button
                            onClick={install}
                            className='mt-2 px-4 py-1.5 bg-brand-600 text-white text-xs font-medium rounded-lg hover:bg-brand-700 transition-colors'
                        >
                            Install
                        </button>
                    </div>
                    <button
                        onClick={dismiss}
                        className='p-1 -mt-1 -mr-1 text-neutral-400 hover:text-neutral-600 rounded-lg'
                        aria-label='Dismiss'
                    >
                        <XMarkIcon className='w-5 h-5' />
                    </button>
                </div>
            </div>
        </div>
    )
}
